// src/api.js

import { getActiveAwareAuthToken } from "./auth/bootstrapTokens";

const configuredApiBase = process.env.REACT_APP_API_BASE?.trim();
const inferredApiBase = `${window.location.protocol}//${window.location.hostname}:8000`;

const API_BASE = configuredApiBase || inferredApiBase;
const tideRequests = new Map();
export const CSRF_COOKIE_NAME = "rp_csrf";
export const CSRF_HEADER_NAME = "X-CSRF-Token";
export const PROTECTED_REQUEST_BODY_NOT_REPLAYABLE =
  "protected_request_body_not_replayable";
const CSRF_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
let memoryCsrfToken = "";

function normalizeCsrfToken(token) {
  return typeof token === "string" ? token.trim() : "";
}

function readCookieValue(cookieName, cookieString) {
  const normalizedCookieName = String(cookieName || "").trim();
  if (!normalizedCookieName || !cookieString) return "";

  const cookiePrefix = `${normalizedCookieName}=`;
  const cookie = cookieString
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(cookiePrefix));

  if (!cookie) return "";

  const rawValue = cookie.slice(cookiePrefix.length);
  try {
    return decodeURIComponent(rawValue);
  } catch {
    return rawValue;
  }
}

export function getCsrfTokenFromCookie() {
  if (typeof document === "undefined") return "";
  return readCookieValue(CSRF_COOKIE_NAME, document.cookie);
}

export function setCsrfToken(token) {
  memoryCsrfToken = normalizeCsrfToken(token);
  return memoryCsrfToken;
}

export function clearCsrfToken() {
  memoryCsrfToken = "";
}

export function getCsrfToken() {
  return memoryCsrfToken || getCsrfTokenFromCookie();
}

function methodNeedsCsrf(method) {
  return CSRF_METHODS.has(String(method || "GET").trim().toUpperCase());
}

function withCsrfHeader(options = {}) {
  if (!methodNeedsCsrf(options.method)) return options;

  const csrfToken = getCsrfToken();
  if (!csrfToken) return options;

  const headers = new Headers(options.headers || {});
  if (!headers.has(CSRF_HEADER_NAME)) {
    headers.set(CSRF_HEADER_NAME, csrfToken);
  }

  return {
    ...options,
    headers,
  };
}

export function withApiCredentials(options = {}) {
  return {
    ...withCsrfHeader(options),
    credentials: "include",
  };
}

export function withProtectedApiCredentials(options = {}) {
  const headers = new Headers(options.headers || {});
  const token = getActiveAwareAuthToken(
    typeof window !== "undefined" ? window : undefined
  );
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return withApiCredentials({
    ...options,
    headers,
  });
}

export class ProtectedRequestReplayError extends Error {
  constructor() {
    super("Protected request body cannot be safely retried.");
    this.name = "ProtectedRequestReplayError";
    this.code = PROTECTED_REQUEST_BODY_NOT_REPLAYABLE;
    this.errorCategory = "request_not_replayable";
    this.isNetworkError = false;
  }
}

function isProtectedRequestBodyReplayable(body) {
  if (body == null || typeof body === "string") {
    return true;
  }

  if (
    (typeof FormData !== "undefined" && body instanceof FormData) ||
    (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) ||
    (typeof Blob !== "undefined" && body instanceof Blob) ||
    (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer)
  ) {
    return true;
  }

  return typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView(body);
}

function throwIfRequestAborted(signal) {
  if (!signal?.aborted) {
    return;
  }

  if (typeof signal.throwIfAborted === "function") {
    signal.throwIfAborted();
  }

  if (signal.reason !== undefined) {
    throw signal.reason;
  }

  const error = new Error("The protected request was aborted.");
  error.name = "AbortError";
  throw error;
}

async function readResponseDetail(response) {
  if (!response || typeof response.clone !== "function") {
    return "";
  }

  try {
    const payload = await response.clone().json();
    return typeof payload?.detail === "string" ? payload.detail.trim() : "";
  } catch {
    return "";
  }
}

async function refreshProtectedSession(signal) {
  throwIfRequestAborted(signal);
  const response = await fetch(
    `${API_BASE}/auth/me`,
    withProtectedApiCredentials({
      method: "GET",
      cache: "no-store",
      signal,
    })
  );
  if (!response.ok) {
    return response;
  }

  try {
    const payload = await response.json();
    if (typeof payload?.csrfToken === "string" && payload.csrfToken.trim()) {
      setCsrfToken(payload.csrfToken);
    }
  } catch {
    // The protected endpoint still receives the current Bearer token, when present.
  }
  return response;
}

export async function protectedApiFetch(url, options = {}) {
  const signal = options.signal;
  throwIfRequestAborted(signal);

  const unsafeRequest = methodNeedsCsrf(options.method);
  const replayableBody = isProtectedRequestBodyReplayable(options.body);
  let requestOptions = withProtectedApiCredentials(options);
  const requestHeaders = new Headers(requestOptions.headers || {});

  if (
    unsafeRequest &&
    !requestHeaders.has("Authorization") &&
    !requestHeaders.has(CSRF_HEADER_NAME)
  ) {
    const sessionResponse = await refreshProtectedSession(signal);
    if (!sessionResponse.ok) {
      return sessionResponse;
    }
    throwIfRequestAborted(signal);
    requestOptions = withProtectedApiCredentials(options);
  }

  throwIfRequestAborted(signal);
  let response = await fetch(url, requestOptions);
  const detail = response.status === 403 ? await readResponseDetail(response) : "";
  if (response.status !== 403 || detail !== "csrf_token_invalid") {
    return response;
  }

  clearCsrfToken();
  const sessionResponse = await refreshProtectedSession(signal);
  if (!sessionResponse.ok) {
    return sessionResponse;
  }

  if (!replayableBody) {
    throw new ProtectedRequestReplayError();
  }

  throwIfRequestAborted(signal);
  response = await fetch(url, withProtectedApiCredentials(options));
  return response;
}

function getTidesRequestKey(lat, lng) {
  return `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;
}

/**
 * Get tide information by location
 * Backend decides provider, region, cache, fallback
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<Object>}
 */
export async function getTidesByLocation(lat, lng) {
  const key = getTidesRequestKey(lat, lng);
  const existingRequest = tideRequests.get(key);

  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    const res = await fetch(`${API_BASE}/tides/?lat=${lat}&lng=${lng}`);

    if (!res.ok) {
      throw new Error("Failed to fetch tides");
    }

    return res.json();
  })();

  tideRequests.set(key, request);

  try {
    return await request;
  } finally {
    if (tideRequests.get(key) === request) {
      tideRequests.delete(key);
    }
  }
}

/**
 * Get weather information by location
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<Object>}
 */
export async function getWeatherByLocation(lat, lng) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const res = await fetch(
      `${API_BASE}/weather/?lat=${lat}&lng=${lng}`,
      { signal: controller.signal }
    );

    if (!res.ok) {
      console.warn("Weather API fetch failed, returning fallback");
      return { weather: null, moon: null, waterTemp: null, depth: null, placeName: "" };
    }

    return await res.json();
  } catch (err) {
    console.error("Weather network error:", err);
    return { weather: null, moon: null, waterTemp: null, depth: null, placeName: "" };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * ðŸ†• Get Species by Location
 * Backend resolves environment (Fresh/Salt) and queries biodiversity APIs.
 * Returns a fallback object if the fetch fails to prevent UI crashes.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<Object>} { country, environment, species: [...] }
 */
export async function getSpeciesByLocation(lat, lng) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout for species

  try {
    const res = await fetch(
      `${API_BASE}/species/by-location?lat=${lat}&lng=${lng}`,
      { signal: controller.signal }
    );

    if (!res.ok) {
      console.warn("Species API fetch failed, returning fallback");
      return { species: [], environment: "freshwater", country: "GLOBAL" };
    }

    return await res.json();
  } catch (err) {
    console.error("Species network error:", err);
    return { species: [], environment: "freshwater", country: "GLOBAL" };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Get historical fishing context (weather, wind, sun, moon, tides, time)
 * Backend guarantees partial-safe response
 *
 * @param {number} lat
 * @param {number} lng
 * @param {string} date - YYYY-MM-DD
 * @param {string} time - HH:MM
 * @returns {Promise<Object|null>}
 */
export async function getHistoricalContext(lat, lng, date, time) {
  console.log("ðŸ“ž getHistoricalContext called with:", { lat, lng, date, time });
  
  if (
    typeof lat !== "number" ||
    typeof lng !== "number" ||
    !date
  ) {
    console.log("âŒ Invalid params:", { lat: typeof lat, lng: typeof lng, date });
    return null;
  }
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  
  try {
    const params = new URLSearchParams({
      coordinates: `${lat},${lng}`,
      date,
    });
    
    if (time) {
      params.append("time", time);
    }
    
    const url = `${API_BASE}/context/historical?${params.toString()}`;
    console.log("ðŸŒ Fetching:", url);
    
    const res = await fetch(url, { signal: controller.signal });
    
    console.log("ðŸ“¡ Response status:", res.status, res.ok);
    
    if (!res.ok) {
      console.log("âŒ Response not OK");
      return null;
    }
    
    const data = await res.json();
    console.log("ðŸ“¦ Response data:", data);
    
    const result = Object.keys(data || {}).length ? data : null;
    console.log("âœ… Returning:", result);
    return result;
  } catch (err) {
    console.error("âŒ ERROR in getHistoricalContext:", err);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Get cached post info (historical context) from backend.
 * - First request: Backend calculates & caches (slower).
 * - Subsequent requests: Backend returns cache (instant).
 *
 * @param {string} postId
 * @param {number} lat
 * @param {number} lng
 * @param {number} utcTimestamp - Milliseconds
 * @returns {Promise<Object|null>}
 */
export async function getPostInfo(postId, lat, lng, utcTimestamp) {
  if (!postId || !lat || !lng || !utcTimestamp) {
    console.warn("âš ï¸ getPostInfo missing params:", { postId, lat, lng, utcTimestamp });
    return null;
  }

  try {
    const params = new URLSearchParams({
      post_id: postId,
      lat: lat.toString(),
      lng: lng.toString(),
      utc_timestamp: utcTimestamp.toString(),
    });

    const url = `${API_BASE}/post-info/?${params.toString()}`;
    console.log("ðŸ“¡ Fetching Post Info:", url);

    const res = await fetch(url);

    if (!res.ok) {
      console.error("âŒ Post Info Error:", res.status);
      return null;
    }

    const data = await res.json();
    console.log("ðŸ“¦ Post Info Received:", data);
    return data;

  } catch (err) {
    console.error("âŒ Network Error in getPostInfo:", err);
    return null;
  }
}

/**
 * ðŸ†• Get Fishing Rules & Regulations
 * Calls the backend to detect country/water type and get legal rules.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<Object>}
 */

const fishingRulesCache = new Map();
const fishingRulesRequests = new Map();
const FISHING_RULES_CLIENT_CACHE_VERSION = "usa_states_phase1";

function getFishingRulesKey(lat, lng) {
  return `${FISHING_RULES_CLIENT_CACHE_VERSION}:${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;
}

function clearFishingRulesRequest(key) {
  const pending = fishingRulesRequests.get(key);
  if (!pending) return;
  clearTimeout(pending.timeoutId);
  fishingRulesRequests.delete(key);
}

export async function getFishingRules(lat, lng) {
  const key = getFishingRulesKey(lat, lng);

  if (fishingRulesCache.has(key)) {
    return fishingRulesCache.get(key);
  }

  const existingRequest = fishingRulesRequests.get(key);
  if (existingRequest) {
    return existingRequest.promise;
  }

  for (const [pendingKey, pending] of fishingRulesRequests.entries()) {
    if (pendingKey === key) continue;
    pending.controller.abort();
    clearFishingRulesRequest(pendingKey);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);

  const promise = (async () => {
    try {
      const res = await fetch(
        `${API_BASE}/fishing-rules/by-location?lat=${lat}&lng=${lng}`,
        { signal: controller.signal }
      );

      if (!res.ok) {
        console.warn("Fishing Rules API fetch failed");
        return null;
      }

      const result = await res.json();
      fishingRulesCache.set(key, result);
      return result;
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Fishing Rules network error:", err);
      }
      return null;
    } finally {
      clearFishingRulesRequest(key);
    }
  })();

  fishingRulesRequests.set(key, {
    promise,
    controller,
    timeoutId,
  });

  return promise;
}

export async function getAnglersByRadius(
  lat,
  lng,
  radiusKm,
  token,
  {
    waterType = "any",
    wayOfFishing = "any",
    fishingMethod = "any",
  } = {}
) {
  const params = new URLSearchParams({
    lat: lat.toString(),
    lng: lng.toString(),
    radiusKm: radiusKm.toString(),
  });

  if (waterType && waterType !== "any") {
    params.set("waterType", waterType);
  }

  if (wayOfFishing && wayOfFishing !== "any") {
    params.set("wayOfFishing", wayOfFishing);
  }

  if (fishingMethod && fishingMethod !== "any") {
    params.set("fishingMethod", fishingMethod);
  }

  const res = await fetch(
    `${API_BASE}/auth/anglers/by-radius?${params.toString()}`,
    withApiCredentials({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
  );

  if (!res.ok) {
    throw new Error("Failed to fetch nearby anglers");
  }

  return res.json();
}

export async function getGroupsByRadius(
  lat,
  lng,
  radiusKm,
  token,
  { waterType = "all", wayOfFishing = "all", typeOfFishing = "all" } = {}
) {
  const params = new URLSearchParams({
    lat: lat.toString(),
    lng: lng.toString(),
    radiusKm: radiusKm.toString(),
  });

  if (waterType && waterType !== "all") {
    params.set("waterType", waterType);
  }

  if (wayOfFishing && wayOfFishing !== "all") {
    params.set("wayOfFishing", wayOfFishing);
  }

  if (typeOfFishing && typeOfFishing !== "all") {
    params.set("typeOfFishing", typeOfFishing);
  }

  const res = await fetch(
    `${API_BASE}/groups/by-radius?${params.toString()}`,
    withApiCredentials({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
  );

  if (!res.ok) {
    throw new Error("Failed to fetch nearby groups");
  }

  return res.json();
}

export async function toggleFavoriteSpot(spot, token) {
  const res = await fetch(
    `${API_BASE}/auth/favorite-spots/toggle`,
    withApiCredentials({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        name: spot?.name || "",
        location: spot?.location || "",
        lat: Number(spot?.lat),
        lng: Number(spot?.lng),
      }),
    })
  );

  if (!res.ok) {
    throw new Error("Failed to update favorite spot");
  }

  return res.json();
}

export function getPostDeleteErrorMessage(error) {
  if (error?.status === 401) {
    return "Your session has expired. Please sign in again.";
  }
  if (error?.status === 403) {
    if (error?.detail === "csrf_token_invalid") {
      return "Your session could not be refreshed. Please try again.";
    }
    return "You do not have permission to delete this post.";
  }
  if (error?.status) {
    return "The post could not be deleted. Please try again.";
  }
  return "Check your internet connection and try again.";
}

export async function deletePost(postId, _token) {
  const normalizedPostId = String(postId || "").trim();
  if (!normalizedPostId) {
    const error = new Error("post_delete_id_required");
    error.category = "validation";
    throw error;
  }

  const res = await protectedApiFetch(
    `${API_BASE}/posts/${encodeURIComponent(normalizedPostId)}`,
    {
      method: "DELETE",
    }
  );

  let responsePayload = null;
  try {
    responsePayload = await res.json();
  } catch {
    responsePayload = null;
  }

  if (res.status === 404) {
    return {
      ok: true,
      postId: normalizedPostId,
      alreadyDeleted: true,
    };
  }

  if (!res.ok) {
    const detail =
      typeof responsePayload?.detail === "string"
        ? responsePayload.detail.trim()
        : "";
    const error = new Error(detail || "post_delete_failed");
    error.status = res.status;
    error.detail = detail;
    error.category =
      res.status === 401
        ? "session_expired"
        : res.status === 403
          ? "permission_denied"
          : "http_error";
    throw error;
  }

  return responsePayload || {
    ok: true,
    postId: normalizedPostId,
  };
}

export async function editPost(postId, payload, token) {
  const res = await fetch(
    `${API_BASE}/posts/${encodeURIComponent(postId)}`,
    withApiCredentials({
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    })
  );

  let responsePayload = null;
  try {
    responsePayload = await res.json();
  } catch {
    responsePayload = null;
  }

  if (!res.ok) {
    const detail =
      typeof responsePayload?.detail === "string" ? responsePayload.detail : "";
    const error = new Error(detail || "post_edit_save_failed");
    error.status = res.status;
    error.detail = detail;
    throw error;
  }

  return responsePayload;
}

export async function replacePostMedia(postId, mediaFile, token) {
  const formData = new FormData();
  formData.append("media", mediaFile);

  const res = await fetch(
    `${API_BASE}/posts/${encodeURIComponent(postId)}/media`,
    withApiCredentials({
      method: "PATCH",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })
  );

  let responsePayload = null;
  try {
    responsePayload = await res.json();
  } catch {
    responsePayload = null;
  }

  if (!res.ok) {
    const detail =
      typeof responsePayload?.detail === "string" ? responsePayload.detail : "";
    const error = new Error(detail || "post_edit_media_replace_failed");
    error.status = res.status;
    error.detail = detail;
    throw error;
  }

  return responsePayload;
}

export async function getCurrentAnnouncement(token) {
  const res = await fetch(
    `${API_BASE}/announcements/current`,
    withApiCredentials({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
  );

  if (!res.ok) {
    throw new Error("Failed to fetch announcement");
  }

  return res.json();
}

export async function dismissCurrentAnnouncement(token) {
  const res = await fetch(
    `${API_BASE}/announcements/current/dismiss`,
    withApiCredentials({
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
  );

  if (!res.ok) {
    throw new Error("Failed to dismiss announcement");
  }

  return res.json();
}

export async function getCurrentSurvey(token, options = {}) {
  const res = await fetch(
    `${API_BASE}/surveys/current`,
    withApiCredentials({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: options.signal,
    })
  );

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const detail =
      typeof payload?.detail === "string" ? payload.detail.trim() : "";
    if (res.status === 401) {
      throw new Error("login_required");
    }
    if (res.status === 404 && detail === "Not Found") {
      throw new Error("surveys_endpoint_not_found");
    }
    throw new Error(detail || "Failed to fetch survey");
  }

  return payload || { survey: null, alreadySubmitted: false };
}

export async function submitCurrentSurvey(token, answers, options = {}) {
  const res = await fetch(
    `${API_BASE}/surveys/current/submit`,
    withApiCredentials({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ answers }),
      signal: options.signal,
    })
  );

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const detail =
      typeof payload?.detail === "string" ? payload.detail.trim() : "";
    if (res.status === 401) {
      throw new Error("login_required");
    }
    throw new Error(detail || "Failed to submit survey");
  }

  return payload || { status: "submitted", submittedAt: "" };
}

async function parseJsonResponse(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function buildApiError(payload, fallbackMessage, status) {
  const detail = typeof payload?.detail === "string" ? payload.detail.trim() : "";
  const error = new Error(detail || fallbackMessage);
  error.status = status;
  error.detail = detail;
  return error;
}

export async function listGearSets(token, options = {}) {
  const res = await fetch(
    `${API_BASE}/gear/sets`,
    withApiCredentials({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: options.signal,
    })
  );
  const payload = await parseJsonResponse(res);

  if (!res.ok) {
    throw buildApiError(payload, "Failed to fetch gear sets", res.status);
  }

  return Array.isArray(payload?.gearSets) ? payload.gearSets : [];
}

export async function createGearSet(token, gearSet, options = {}) {
  const res = await fetch(
    `${API_BASE}/gear/sets`,
    withApiCredentials({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(gearSet || {}),
      signal: options.signal,
    })
  );
  const payload = await parseJsonResponse(res);

  if (!res.ok) {
    throw buildApiError(payload, "Failed to create gear set", res.status);
  }

  return payload;
}

export async function updateGearSet(token, gearId, gearSet, options = {}) {
  const res = await fetch(
    `${API_BASE}/gear/sets/${encodeURIComponent(gearId)}`,
    withApiCredentials({
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(gearSet || {}),
      signal: options.signal,
    })
  );
  const payload = await parseJsonResponse(res);

  if (!res.ok) {
    throw buildApiError(payload, "Failed to update gear set", res.status);
  }

  return payload;
}

export async function deleteGearSet(token, gearId, options = {}) {
  const res = await fetch(
    `${API_BASE}/gear/sets/${encodeURIComponent(gearId)}`,
    withApiCredentials({
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: options.signal,
    })
  );
  const payload = await parseJsonResponse(res);

  if (!res.ok) {
    throw buildApiError(payload, "Failed to delete gear set", res.status);
  }

  return payload || { status: "deleted", id: gearId };
}

export async function deleteCurrentAccount(token) {
  const res = await fetch(
    `${API_BASE}/auth/account`,
    withApiCredentials({
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
  );

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const detail =
      typeof payload?.detail === "string" ? payload.detail.trim() : "";
    throw new Error(
      detail && !detail.includes("_") ? detail : "Failed to delete account"
    );
  }

  return payload || { deleted: true, authCleanupDeferred: false };
}

export async function inspectVideoPreviewMetadata(file, options = {}) {
  if (!file) {
    throw new Error("missing_video_file");
  }

  const formData = new FormData();
  formData.append(
    "media",
    file,
    typeof file?.name === "string" && file.name ? file.name : "video-upload"
  );

  const token = getActiveAwareAuthToken(
    typeof window !== "undefined" ? window : undefined
  );
  const res = await fetch(
    `${API_BASE}/media/inspect/video-preview`,
    withApiCredentials({
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
      signal: options.signal,
    })
  );

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const detail =
      typeof payload?.detail === "string" ? payload.detail.trim() : "";
    throw new Error(detail || "Failed to inspect video preview metadata");
  }

  return {
    capturedAt:
      typeof payload?.capturedAt === "string" && payload.capturedAt.trim()
        ? payload.capturedAt.trim()
        : null,
    warnings: Array.isArray(payload?.warnings) ? payload.warnings : [],
  };
}

export default API_BASE;
